import { Box, Button, Chip, CircularProgress, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, Switch, TextField, Typography } from '@mui/material';
import { KeyboardEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminGetBlog, createBlog, updateBlog } from '../../api/blogs';
import { useApiCallWithAuth } from '../../hooks/useApiCallWithAuth';
import { MarkdownView } from '../../components/MarkdownView';
import { ImageUploadButton } from '../../components/ImageUploadButton';
import { LogoUploadButton } from '../../components/LogoUploadButton';
import { ZipImportButton, ZipPostData } from '../../components/ZipImportButton';
import { SocialSchedulePanel } from './socialSchedulePanel';

interface BlogFormState {
  title: string;
  imageUrl: string;
  logoUrl: string;
  shortDescription: string;
  content: string;
  tagInput: string;
  tags: string[];
  datePosted: string;
  isPublished: boolean;
  domain: string;
}

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const empty: BlogFormState = {
  title: '',
  imageUrl: '',
  logoUrl: '',
  shortDescription: '',
  content: '',
  tagInput: '',
  tags: [],
  datePosted: todayLocal(),
  isPublished: true,
  domain: 'General',
};

export function BlogEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const blogId = id ? parseInt(id, 10) : null;

  const { request: getRequest } = useApiCallWithAuth(adminGetBlog);
  const { request: createRequest } = useApiCallWithAuth(createBlog);
  const { request: updateRequest } = useApiCallWithAuth(updateBlog);

  const [form, setForm] = useState<BlogFormState>(empty);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || blogId === null || isNaN(blogId)) return;
    getRequest(blogId)
      .then(data => {
        const dp = data.datePosted ?? todayLocal();
        setForm({
          title: data.title,
          imageUrl: data.imageUrl ?? '',
          logoUrl: data.logoUrl ?? '',
          shortDescription: data.shortDescription ?? '',
          content: data.content,
          tagInput: '',
          tags: data.tags,
          datePosted: dp,
          isPublished: data.isPublished ?? true,
          domain: data.domain ?? 'General',
        });
      })
      .catch(() => setError('Failed to load article'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogId, isEdit]);

  function set(field: keyof BlogFormState, value: string | string[]) {
    setForm(f => ({ ...f, [field]: value }));
  }

  function addTag() {
    const tag = form.tagInput.trim().toLowerCase().replace(/^#/, '');
    if (tag && !form.tags.includes(tag)) {
      set('tags', [...form.tags, tag]);
    }
    set('tagInput', '');
  }

  function handleTagKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  }

  function removeTag(tag: string) {
    set('tags', form.tags.filter(t => t !== tag));
  }

  function handleImport(data: ZipPostData) {
    setForm(f => ({
      ...f,
      title: data.title,
      imageUrl: data.imageUrl,
      logoUrl: data.logoUrl,
      shortDescription: data.shortDescription,
      domain: data.domain,
      tags: data.tags,
    }));
  }

  async function handleSave() {
    setError(null);
    if (!form.title.trim() || !form.content.trim() || !form.shortDescription.trim()) {
      setError('Title, short description, and content are required.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title,
        imageUrl: form.imageUrl || undefined,
        logoUrl: form.logoUrl || undefined,
        shortDescription: form.shortDescription,
        content: form.content,
        tags: form.tags,
        datePosted: form.datePosted ? new Date(form.datePosted).toISOString() : undefined,
        isPublished: form.isPublished,
        domain: form.domain,
      };

      if (isEdit && blogId !== null && !isNaN(blogId)) {
        await updateRequest(blogId, payload);
        navigate('/blog/admin');
      } else {
        // Land on the edit route (not the admin list) so the social scheduling panel,
        // which needs a saved blogId, is usable immediately after first save.
        const { blogId: newBlogId } = await createRequest(payload);
        navigate(`/blog/${newBlogId}/edit`);
      }
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 3 }}>
        <Typography variant='h5' fontWeight={700}>{isEdit ? 'Edit Post' : 'New Post'}</Typography>
        <Stack direction='row' spacing={1} alignItems='flex-start'>
          {!isEdit && <ZipImportButton onImport={handleImport} />}
          <Button variant='outlined' onClick={() => setPreview(p => !p)}>
            {preview ? 'Edit' : 'Preview'}
          </Button>
          <Button variant='outlined' onClick={() => navigate('/blog/admin')}>Cancel</Button>
          <Button variant='contained' onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Stack>
      </Stack>

      {error && <Typography color='error' sx={{ mb: 2 }}>{error}</Typography>}

      {preview ? (
        <Box>
          {form.imageUrl && (
            <Box component='img' src={form.imageUrl} alt={form.title}
              sx={{ width: '100%', maxHeight: 380, objectFit: 'cover', borderRadius: 2, mb: 3 }} />
          )}
          {form.logoUrl && (
            <Box component='img' src={form.logoUrl} alt='Logo'
              sx={{ width: 250, height: 250, objectFit: 'contain', borderRadius: 1, mb: 2 }} />
          )}
          <Typography variant='h3' fontWeight={700} sx={{ mb: 2 }}>{form.title || '(No title)'}</Typography>
          <Divider sx={{ mb: 3 }} />
          <MarkdownView>{form.content}</MarkdownView>
        </Box>
      ) : (
        <Stack spacing={2.5}>
          <TextField
            label='Title'
            value={form.title}
            onChange={e => set('title', e.target.value)}
            fullWidth
            required
          />

          <Stack direction='row' spacing={1} alignItems='flex-start'>
            <TextField
              label='Image URL'
              value={form.imageUrl}
              onChange={e => set('imageUrl', e.target.value)}
              fullWidth
              placeholder='https://...'
            />
            <Box sx={{ pt: 1 }}>
              <ImageUploadButton onUpload={url => set('imageUrl', url)} />
            </Box>
          </Stack>

          <Stack direction='row' spacing={1} alignItems='flex-start'>
            <TextField
              label='Logo URL'
              value={form.logoUrl}
              onChange={e => set('logoUrl', e.target.value)}
              fullWidth
              placeholder='https://...'
            />
            <Box sx={{ pt: 1 }}>
              <LogoUploadButton onUpload={url => set('logoUrl', url)} />
            </Box>
          </Stack>

          {form.logoUrl && (
            <Box>
              <Box component='img' src={form.logoUrl} alt='Logo preview'
                sx={{ width: 80, height: 80, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
            </Box>
          )}

          <TextField
            label='Short Description'
            helperText='Internal only — used for email digests, not shown on the blog'
            value={form.shortDescription}
            onChange={e => set('shortDescription', e.target.value)}
            fullWidth
            multiline
            rows={2}
            required
          />

          <TextField
            label='Date Posted'
            type='date'
            value={form.datePosted}
            onChange={e => set('datePosted', e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <FormControl fullWidth>
            <InputLabel id='domain-label'>Domain</InputLabel>
            <Select
              labelId='domain-label'
              value={form.domain}
              label='Domain'
              onChange={e => set('domain', e.target.value)}
            >
              <MenuItem value='General'>General</MenuItem>
              <MenuItem value='Sports'>Sports</MenuItem>
              <MenuItem value='Running'>Running</MenuItem>
              <MenuItem value='Food'>Food</MenuItem>
            </Select>
          </FormControl>

          <Box>
            <TextField
              label='Tags'
              helperText='Type a tag and press Enter or comma to add'
              value={form.tagInput}
              onChange={e => set('tagInput', e.target.value)}
              onKeyDown={handleTagKeyDown}
              onBlur={addTag}
              fullWidth
              placeholder='e.g. typescript'
            />
            {form.tags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {form.tags.map(tag => (
                  <Chip key={tag} label={`#${tag}`} size='small' onDelete={() => removeTag(tag)} />
                ))}
              </Box>
            )}
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={form.isPublished}
                onChange={e => setForm(f => ({ ...f, isPublished: e.target.checked }))}
              />
            }
            label={form.isPublished ? 'Published' : 'Draft'}
          />

          <TextField
            label='Content (Markdown)'
            value={form.content}
            onChange={e => set('content', e.target.value)}
            fullWidth
            multiline
            rows={20}
            required
            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
          />
        </Stack>
      )}

      {isEdit && blogId !== null && !isNaN(blogId) && <SocialSchedulePanel blogId={blogId} />}
    </Box>
  );
}
